import type { Virtualizer } from '@tanstack/react-virtual';
import { useEffect, useRef } from 'react';
import { useMountedState, useSessionStorage } from 'react-use';
import { useDebouncedCallback } from 'use-debounce';

const SCROLL_REPOSITION_DELAY_MS = 100;

export const useIndexBasedScrollRestoration = (
  id: string,
  virtualizer: Virtualizer<HTMLDivElement, Element>,
  restoreScrollPosition = true,
) => {
  const isMounted = useMountedState();
  const initialScrollRef = useRef(false);
  const [lastSavedItemIndex, setLastSavedItemIndex] = useSessionStorage<number>(
    `axpx.scrollRestoration.${id}`,
    0,
  );

  useEffect(() => {
    if (!restoreScrollPosition || initialScrollRef.current) return;

    initialScrollRef.current = true;

    if (lastSavedItemIndex > 0) {
      // Initial scroll
      virtualizer.scrollToIndex(lastSavedItemIndex, { align: 'start', behavior: 'auto' });

      // Perform a second scroll after a short delay, to account for any initial rendering and measurement inaccuracies
      setTimeout(() => {
        if (isMounted()) {
          virtualizer.scrollToIndex(lastSavedItemIndex, { align: 'start', behavior: 'auto' });
        }
      }, SCROLL_REPOSITION_DELAY_MS);
    }
  }, [virtualizer, isMounted, restoreScrollPosition, lastSavedItemIndex]);

  const debouncedScroll = useDebouncedCallback(() => {
    const visibleRange = virtualizer.range;

    if (visibleRange) {
      setLastSavedItemIndex(visibleRange.startIndex);
    }
  });

  useEffect(() => {
    const { scrollElement } = virtualizer;

    if (scrollElement) {
      scrollElement.addEventListener('scroll', debouncedScroll);
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', debouncedScroll);
      }
    };
  }, [virtualizer, debouncedScroll]);
};
